import { api } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { short, inr, pct, date, slotColour } from '../lib/fmt';
import { RankedBars, StackedColumns, StackBar } from '../components/charts';
import { Card, PageHead, Tile, Tiles, Note, Table, Th, Td, TableWrap, Empty, Tag } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton,
         BarsSkeleton, ColumnsSkeleton, Bone } from '../components/skeleton';
import { Link } from 'react-router-dom';
import type { AccountBalance } from '../lib/types';

interface Dash {
  headline: {
    net_revenue: number; gross_billed: number; recurring_share: number;
    outstanding: number; overdue: number; avg_month: number; documents: number;
    months_billed: number; top_client: string | null; top_client_share: number; hhi: number;
  };
  by_service: { name: string; amount: number }[];
  by_client: { name: string; amount: number; docs: number }[];
  matrix: { months: string[]; lines: string[]; grid: Record<string, number[]>; totals: number[] };
  concentration: { groups: { name: string; amount: number; share: number }[] };
  ageing: { as_of: string; total: number; buckets: { key: string; label: string; amount: number; count: number }[] };
  recurring: { monthly: number; annualised: number; plans: { _id: string; name: string; client?: { name: string }; cadence: string; next_run: string; amount: number; currency: string }[] };
  accounts: AccountBalance[];
  bills_due: { _id: string; name: string; amount: number }[];
  claims: { submitted: number; submitted_value: number; approved: number; approved_value: number } | null;
  pending_approvals: number;
}

const AGE: Record<string, string> = {
  current: 'var(--color-s1)', d1_30: 'var(--color-warning)',
  d31_60: 'var(--color-serious)', d61_90: 'var(--color-critical)', d90p: 'var(--color-critical)',
};

/** Every section of the dashboard, in the order and at the size it arrives. */
function DashboardSkeleton() {
  return (
    <Loading label="Loading the dashboard">
      <HeadSkeleton actions={1} />
      <TilesSkeleton />
      <CardSkeleton><BarsSkeleton rows={5} /></CardSkeleton>
      <CardSkeleton className="mt-5"><ColumnsSkeleton /></CardSkeleton>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <CardSkeleton><BarsSkeleton rows={6} labelWidth={190} /></CardSkeleton>
        <CardSkeleton>
          <Bone h={22} radius={3} />
          <div className="mt-3.5">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 py-2.5
                                      border-b border-rule-soft last:border-0">
                <Bone w={132} h={11} />
                <Bone w={70} h={11} />
              </div>
            ))}
          </div>
        </CardSkeleton>
      </div>
      <CardSkeleton className="mt-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="border border-rule rounded p-3.5">
              <Bone w={110} h={10} />
              <Bone w={96} h={20} className="mt-2.5" />
            </div>
          ))}
        </div>
      </CardSkeleton>
      <CardSkeleton flush className="mt-5"><TableSkeleton rows={4} cols={4} /></CardSkeleton>
      <CardSkeleton flush className="mt-5"><TableSkeleton rows={5} cols={5} /></CardSkeleton>
    </Loading>
  );
}

export default function Dashboard() {
  const { fy, meta } = useSession();
  const { data, loading, error } = useAsync<Dash>(
    () => api.get('/api/reports/dashboard', { fy }), [fy]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <Note title="That did not load" tone="critical">{error}</Note>;
  if (!data) return null;

  const h = data.headline;
  const colourFor = (name: string) => {
    const m = meta?.service_lines.find((s) => s.name === name);
    return m?.colour ?? slotColour(data.by_service.findIndex((s) => s.name === name));
  };
  const cash = data.accounts.reduce((s, a) => s + a.balance, 0);

  return (
    <div>
      <PageHead
        eyebrow={`${meta?.org.legal_name ?? ''} · sales & receivables`}
        title={`FY ${fy ?? ''} at a glance`}
        dek={h.documents
          ? `${h.documents} documents across ${data.by_client.length} billing units, ${h.months_billed} months billed.`
          : 'No invoices raised in this financial year yet.'}
        actions={<Link to="/invoices" className="text-[13px] text-ink-2 no-underline hover:text-ink inline-flex items-center min-h-[44px] md:min-h-0">All invoices →</Link>}
      />

      <Tiles>
        <Tile hero label="Net sales, taxable value" value={short(h.net_revenue)}
              sub={<>Excludes GST. <span className="text-muted">Gross billed {short(h.gross_billed)}</span>.</>} />
        <Tile label="Recurring retainers" value={pct(h.recurring_share, 0)}
              sub={<span className="text-muted">of net sales — the share a buyer pays a multiple for</span>} />
        <Tile label="Outstanding" value={short(h.outstanding)}
              sub={h.overdue > 0
                ? <><b className="text-critical">{short(h.overdue)}</b> <span className="text-muted">of it overdue</span></>
                : <span className="text-muted">nothing overdue</span>} />
        <Tile label="Cash on hand" value={short(cash)}
              sub={<span className="text-muted">across {data.accounts.length} accounts</span>} />
      </Tiles>

      {h.top_client_share >= 0.5 && (
        <Note title="Concentration risk" tone="critical">
          <b>{h.top_client}</b> is <b>{pct(h.top_client_share)}</b> of net sales this year. Every other
          client together is <b>{short(h.net_revenue - (data.concentration.groups[0]?.amount ?? 0))}</b>.
          {' '}Herfindahl index {h.hhi.toFixed(2)} — 1.00 would be a single client.
        </Note>
      )}

      <Card title="What we sold" sub="Net of GST and credit notes, by service line.">
        <RankedBars total={h.net_revenue}
          rows={data.by_service.map((s) => ({ ...s, colour: colourFor(s.name) }))} />
      </Card>

      <Card title="Month by month" sub="Billed on invoice date, split by service line." className="mt-5">
        <StackedColumns {...data.matrix} colourFor={colourFor} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <Card title="Who paid" sub="Billing units, largest first.">
          <RankedBars total={h.net_revenue} labelWidth={190}
            rows={data.by_client.map((c) => ({ name: c.name, amount: c.amount }))}
            detail={(r) => [['Billed', short(r.amount)],
                            ['Documents', String(data.by_client.find((c) => c.name === r.name)?.docs ?? 0)]]} />
        </Card>

        <Card title="Receivables ageing" sub={`As at ${date(data.ageing.as_of)}.`}>
          {data.ageing.total === 0 ? (
            <Empty>Nothing outstanding. Every invoice is settled.</Empty>
          ) : (
            <>
              <StackBar parts={data.ageing.buckets.map((b) => ({
                label: b.label, amount: b.amount, colour: AGE[b.key] ?? 'var(--color-s1)' }))} />
              <div className="mt-3.5">
                {data.ageing.buckets.filter((b) => b.count > 0).map((b) => (
                  <div key={b.key} className="flex items-baseline justify-between gap-3 py-2.5
                                              border-b border-rule-soft last:border-0 text-[13px]">
                    <span className="flex items-center gap-2">
                      <i className="w-2 h-2 rounded-sm" style={{ background: AGE[b.key] }} />
                      {b.label} <Tag>{b.count} inv</Tag>
                    </span>
                    <b className="tnum font-semibold">{short(b.amount)}</b>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {data.accounts.length > 0 && (
        <Card title="Cash position" sub="What is actually in the accounts, as opposed to what has been billed."
              className="mt-5"
              actions={<Link to="/accounts" className="text-xs text-ink-2 no-underline hover:text-ink inline-flex items-center min-h-[44px] md:min-h-0">All accounts →</Link>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {data.accounts.map((a) => (
              <div key={a.id} className="border border-rule rounded p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0"><b className="truncate">{a.name}</b> <Tag>{a.kind}</Tag></span>
                  <span className={`font-display text-[19px] font-semibold tnum whitespace-nowrap
                                    ${a.balance < 0 ? 'text-critical' : ''}`}>{inr(a.balance)}</span>
                </div>
                <p className="mt-1.5 m-0 font-mono text-[11.5px] text-muted">
                  {a.txn_count} entries{a.holder ? ` · ${a.holder}` : ''}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(data.pending_approvals > 0 || data.bills_due.length > 0 || (data.claims?.approved ?? 0) > 0) && (
        <Card title="Needs a decision" sub="Things sitting in a queue waiting for a person." className="mt-5" flush>
          <TableWrap>
            <Table>
              <tbody>
                {data.pending_approvals > 0 && (
                  <tr><Td>Expense claims waiting on you</Td>
                      <Td n><b>{data.pending_approvals}</b></Td>
                      <Td className="text-right w-28"><Link to="/expenses" className="text-s1 no-underline">Review</Link></Td></tr>
                )}
                {data.bills_due.length > 0 && (
                  <tr><Td>Bills due in the next 10 days</Td>
                      <Td n><b>{short(data.bills_due.reduce((s, b) => s + b.amount, 0))}</b></Td>
                      <Td className="text-right"><Link to="/bills" className="text-s1 no-underline">Pay</Link></Td></tr>
                )}
                {(data.claims?.approved ?? 0) > 0 && (
                  <tr><Td>Approved claims not yet paid</Td>
                      <Td n><b>{inr(data.claims!.approved_value)}</b></Td>
                      <Td className="text-right"><Link to="/expenses" className="text-s1 no-underline">Disburse</Link></Td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {data.recurring.plans.length > 0 && (
        <Card className="mt-5" flush
              title="Retainer book"
              sub={<><b>{short(data.recurring.monthly)}</b> a month contracted, <b>{short(data.recurring.annualised)}</b> annualised
                    across {data.recurring.plans.length} active plans.</>}
              actions={<Link to="/retainers" className="text-xs text-ink-2 no-underline hover:text-ink inline-flex items-center min-h-[44px] md:min-h-0">Manage →</Link>}>
          <TableWrap>
            <Table>
              <thead><tr><Th>Plan</Th><Th>Client</Th><Th>Cadence</Th><Th>Next raise</Th><Th n>Amount</Th></tr></thead>
              <tbody>
                {data.recurring.plans.map((p) => (
                  <tr key={p._id}>
                    <Td>{p.name}</Td>
                    <Td>{p.client?.name ?? '—'}</Td>
                    <Td><Tag>{p.cadence}</Tag></Td>
                    <Td className="font-mono text-[11.5px]">{date(p.next_run)}</Td>
                    <Td n>{p.currency === 'INR' ? inr(p.amount) : `$${(p.amount / 100).toFixed(2)}`}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}
